import { chainComparators, getNumericComparator, reverseOrder } from "utils/collect";
export function isReasoningStep(chunk) {
    return typeof chunk !== "string" && (chunk.type === "think" || chunk.type === "answer");
}
export function hasOutputContent(message) {
    return message.id_list !== undefined || message.response_data !== undefined;
}
export function getMessageOutputContent(message) {
    if (message.id_list !== undefined) {
        return message.id_list.reduce((a, b) => a + (typeof b === "string" ? b : (b.text ?? "")), "");
    }
    return message.response_data?.output.content;
}
export function getMessageContentReferenceData(message) {
    if (message.id_list !== undefined) {
        return message.id_list?.filter((f) => typeof f !== "string");
    }
    else if (message.content_references === undefined) {
        return [];
    }
    return message.content_references.map((m) => m.reference);
}
function getReasoningStepChunks(type, text, result, userId) {
    const resultData = result?.data;
    const userOutputFeedback = (resultData?.output_feedback ?? []).find((f) => f.user_id == userId);
    const regex = type === "think" ? /(<think>(.|\n)*?<\/think>)/g : /(<answer>(.|\n)*?<\/answer>)/g;
    return Array.from(text.matchAll(regex).map((match) => {
        const start = match.index ?? 0;
        const end = start + match[0].length;
        const step = userOutputFeedback?.feedback_items.find((item) => item.start_index == start && item.end_index == end);
        return {
            type: type,
            chunks: [match[0]],
            start_index: start,
            end_index: end,
            score: step?.score ?? 0,
            comment: step?.comment
        };
    }));
}
export function getSplitMessageOutputContentFromMessage(message, result, userId) {
    if (message.id_list !== undefined) {
        return message.id_list;
    }
    const response = message.response_data;
    if (response === undefined) {
        return [];
    }
    return getSplitMessageOutputContent(response.output, message.content_references ?? [], result, userId);
}
const SEGMENTER = new Intl.Segmenter(navigator.languages, { granularity: "grapheme" });
export function getSplitMessageOutputContent(output, contentReferences, result, userId) {
    const text = output?.content ?? "";
    const splitText = Array.from(SEGMENTER.segment(text), (segment) => segment.segment);
    const reasoningStepChunks = getReasoningStepChunks("think", text, result, userId).concat(getReasoningStepChunks("answer", text, result, userId));
    const chunks = reasoningStepChunks.concat(contentReferences ?? []);
    chunks.sort(chainComparators([
        getNumericComparator((x) => x.start_index),
        reverseOrder(getNumericComparator((x) => x.end_index))
    ]));
    let thinkingStepIndex = 0;
    const thinkingSteps = chunks.filter((chunk) => "type" in chunk).filter((chunk) => chunk.type === "think");
    if (thinkingSteps.length > 1) {
        for (const chunk of thinkingSteps) {
            chunk.index = thinkingStepIndex;
            thinkingStepIndex += 1;
        }
    }
    if (thinkingSteps.length > 0) {
        thinkingSteps[thinkingSteps.length - 1].isLastThinkingStep = true;
    }
    const content = [];
    let lastNonStringChunk = undefined;
    for (const chunk of chunks) {
        const lastIndex = lastNonStringChunk?.end_index ?? 0;
        if (chunk.start_index > lastIndex) {
            content.push(splitText.slice(lastIndex, chunk.start_index).join(""));
        }
        if ("type" in chunk) {
            content.push(chunk);
        }
        else {
            if (chunk.end_index < lastIndex && lastNonStringChunk !== undefined && "type" in lastNonStringChunk) {
                const tail = lastNonStringChunk.chunks.pop();
                if (typeof tail !== "string") {
                    content.push({
                        ...chunk.reference,
                        text: splitText.slice(chunk.start_index, chunk.end_index).join("")
                    });
                }
                else {
                    const splitTail = Array.from(SEGMENTER.segment(tail), (segment) => segment.segment);
                    const offset = lastNonStringChunk.start_index +
                        (lastNonStringChunk.end_index - lastNonStringChunk.start_index - tail.length);
                    const offsetStartIndex = chunk.start_index - offset;
                    const offsetEndIndex = chunk.end_index - offset;
                    if (offsetStartIndex > 0) {
                        lastNonStringChunk.chunks.push(splitTail.slice(0, offsetStartIndex).join(""));
                    }
                    lastNonStringChunk.chunks.push({
                        ...chunk.reference,
                        start_index: chunk.start_index,
                        end_index: chunk.end_index,
                        text: splitTail.slice(offsetStartIndex, offsetEndIndex).join("")
                    });
                    const remainingTail = splitTail.slice(offsetEndIndex).join("");
                    if (remainingTail) {
                        lastNonStringChunk.chunks.push(remainingTail);
                    }
                }
            }
            else {
                content.push({
                    ...chunk.reference,
                    start_index: chunk.start_index,
                    end_index: chunk.end_index,
                    text: splitText.slice(chunk.start_index, chunk.end_index).join("")
                });
            }
        }
        if (chunk.end_index >= lastIndex) {
            lastNonStringChunk = chunk;
        }
    }
    const lastTextString = splitText.slice(lastNonStringChunk?.end_index ?? 0).join("");
    if (lastTextString) {
        content.push(lastTextString);
    }
    const cleanContent = [];
    for (let i = 0; i < content.length; i++) {
        const current = content[i];
        const last = content[i - 1];
        const next = content[i + 1];
        if (last !== undefined &&
            next !== undefined &&
            isReasoningStep(last) &&
            isReasoningStep(next) &&
            typeof current === "string" &&
            current.trim() == "") {
            continue;
        }
        cleanContent.push(current);
    }
    return cleanContent;
}
